import { useEffect, useMemo, useState, type CSSProperties } from "react";
import type { HolidayTabletConfig } from "../scenario/types";
import { HolidayRogueHunt } from "./HolidayRogueHunt";

type Props = {
  config: HolidayTabletConfig;
  onComplete: (outcome?: string) => void;
  onStageChange?: (stage: string) => void;
  style?: CSSProperties;
};

const APPS = ["Mail", "Browser", "Teams", "LinkedIn", "Files", "Calendar"];
const TABS = ["About", "Experience", "Posts", "Connections", "Company"];

const ROGUE_CERT = {
  subject: "CN = mail.google.com",
  issuer: "CN = localhost",
  org: "O = Hotel_Guest-Free Captive Portal CA",
  notGoogle: "Not Google Trust Services. A certificate the rogue access point minted on this Wi-Fi.",
  valid: "20 Aug 2026 11:02 — 21 Aug 2026 11:02",
  serial: "00:9F:2C:18:E4:71:A0:6B",
  sha256: "C3 91 4A 08 7E B2 55 1D  90 6C 2F 11 44 AE 08 3B\n7A 19 D0 62 8C F4 0E 27  51 BB 6A 03 9D 70 14 8E",
};

function WifiIcon({ signal = 3 }: { signal?: number }) {
  return (
    <svg className="ht-wifi-icon" viewBox="0 0 24 18" aria-hidden>
      <path className={signal >= 1 ? "on" : ""} d="M9.4 14.4a3.8 3.8 0 0 1 5.2 0" />
      <path className={signal >= 2 ? "on" : ""} d="M6.1 10.9a8.3 8.3 0 0 1 11.8 0" />
      <path className={signal >= 3 ? "on" : ""} d="M2.7 7.3a13.1 13.1 0 0 1 18.6 0" />
      <circle className="on" cx="12" cy="17" r="1.2" />
    </svg>
  );
}

function LockIcon() {
  return <svg className="ht-lock-icon" viewBox="0 0 16 18" aria-label="Password protected"><path d="M4 8V5a4 4 0 0 1 8 0v3" /><rect x="2" y="8" width="12" height="9" rx="2" /></svg>;
}

function MailIcon() {
  return <svg viewBox="0 0 40 40" aria-hidden><rect x="4" y="8" width="32" height="24" rx="5" /><path d="m6 11 14 11 14-11" /></svg>;
}

function SslLock({ bad }: { bad: boolean }) {
  return (
    <svg className="ht-ssl-lock-ico" viewBox="0 0 20 20" aria-hidden>
      <path d="M6.2 9.2V7.1a3.8 3.8 0 0 1 7.6 0v2.1" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <rect x="4.2" y="9" width="11.6" height="8.2" rx="1.6" fill="currentColor" />
      {bad && <path d="M5 4.2 16 16.4" stroke="#fff" strokeWidth="1.8" />}
    </svg>
  );
}

export function HolidayTablet({ config, onComplete, onStageChange, style }: Props) {
  const initial =
    config.mode === "wifi"
      ? "lost"
      : config.mode === "inbox_linkedin"
        ? "desktop"
        : config.mode === "linkedin_profile"
          ? "profile"
          : config.mode === "home"
            ? "home"
            : "reveal";
  const [stage, setStage] = useState(initial);
  const [ignored, setIgnored] = useState(false);
  const [tab, setTab] = useState("About");
  const [connected, setConnected] = useState(config.mode !== "wifi");
  const [rogueNote, setRogueNote] = useState<string | null>(null);
  const [sslError, setSslError] = useState(false);
  const [lockOpen, setLockOpen] = useState(false);
  const [certOpen, setCertOpen] = useState(false);
  const [sawCert, setSawCert] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [safetyNote, setSafetyNote] = useState(false);
  const [seenTabs, setSeenTabs] = useState<Set<string>>(() => new Set(["About"]));

  useEffect(() => {
    setStage(initial);
    setConnected(config.mode !== "wifi");
    setSslError(false);
    setLockOpen(false);
    setCertOpen(false);
    setSawCert(false);
    setAdvancedOpen(false);
    setSafetyNote(false);
    setRogueNote(null);
    setSeenTabs(new Set(["About"]));
  }, [config.mode, initial]);

  useEffect(() => {
    if (stage === "hunt") return;
    if (stage !== "wifi_ssl") {
      onStageChange?.(stage);
      return;
    }
    if (!sslError) onStageChange?.("wifi_ssl");
    else if (certOpen) onStageChange?.("wifi_ssl_cert");
    else if (lockOpen) onStageChange?.("wifi_ssl_lock");
    else if (sawCert && advancedOpen) onStageChange?.("wifi_ssl_proceed");
    else if (sawCert) onStageChange?.("wifi_ssl_advanced");
    else onStageChange?.("wifi_ssl_warn");
  }, [stage, sslError, lockOpen, certOpen, sawCert, advancedOpen, onStageChange]);

  const profile = config.profile;
  const networks = useMemo(
    () =>
      config.networks ?? [
          { id: "guest", name: "Hotel_Guest", locked: false, signal: 4 },
          { id: "rogue", name: "Hotel_Guest-Free", locked: false, signal: 5 },
          { id: "staff", name: "OceanResort_Staff", locked: true, signal: 4 },
        { id: "pos", name: "PoolBar_POS", locked: true, signal: 3 },
        { id: "admin", name: "OceanResort_Admin", locked: true, signal: 2 },
        { id: "villa", name: "Villa_Management", locked: true, signal: 2 },
      ],
    [config.networks],
  );

  const join = (id: string, locked: boolean) => {
    if (locked) return;
    if (id !== "guest") {
      setRogueNote("Almost the same name. That one is not the hotel network.");
      window.setTimeout(() => setRogueNote(null), 2400);
      return;
    }
    setRogueNote(null);
    setStage("connecting");
    window.setTimeout(() => {
      setConnected(true);
      setStage("wifi_risk");
    }, 1150);
  };

  useEffect(() => {
    const handleGuideAction = (event: Event) => {
      const requestedStage = (event as CustomEvent<{ stage?: string }>).detail?.stage;
      const sslSub = stage === "wifi_ssl" && !!requestedStage?.startsWith("wifi_ssl");
      if (requestedStage !== stage && !sslSub) return;
      if (stage === "lost") setStage("wifi");
      else if (stage === "wifi") join("guest", false);
      else if (stage === "wifi_risk") setStage("wifi_ssl");
      else if (stage === "wifi_ssl") {
        if (!sslError) setSslError(true);
        else if (lockOpen && !certOpen) {
          setCertOpen(true);
          setSawCert(true);
        } else if (certOpen) {
          setCertOpen(false);
          setLockOpen(false);
        } else if (sawCert && !advancedOpen) setAdvancedOpen(true);
        else setStage("connected");
      }
      else if (stage === "connected") setStage("hunt");
      else if (stage === "desktop") setStage("inbox");
      else if (stage === "inbox") setStage("mail");
      else if (stage === "mail") onComplete("view_profile");
      else if (stage === "profile") {
        if (seenTabs.size >= TABS.length) setStage("messages");
      }
      else if (stage === "messages") setStage("conversation");
      else if (stage === "conversation") setStage("payoff");
      else if (stage === "ignored_note") setStage("messages");
      else if (stage === "payoff") onComplete(ignored ? "ignored" : "accepted");
      else if (stage === "home") onComplete("open_game");
    };
    window.addEventListener("cf-holiday-guide-action", handleGuideAction);
    return () => window.removeEventListener("cf-holiday-guide-action", handleGuideAction);
  }, [onComplete, stage, sslError, lockOpen, certOpen, advancedOpen, sawCert, seenTabs, ignored]);

  const profileBody =
    tab === "About" ? (
      <>
        <p>{profile?.about}</p>
        {profile?.joined && <small className="ht-about-meta">About this profile · {profile.joined}</small>}
      </>
    ) : tab === "Experience" ? (
      <div className="ht-list">
        {profile?.experience.map((x) => (
          <div className="ht-row" key={`${x.role}-${x.company}`}>
            <span className="ht-company-mark">{x.company.slice(0, 1)}</span>
            <div><strong>{x.role}</strong><small>{x.company} · {x.years}</small></div>
          </div>
        ))}
      </div>
    ) : tab === "Posts" ? (
      <div className="ht-posts">
        {profile?.postBurstNote && <div className="ht-post-burst">{profile.postBurstNote}</div>}
        {profile?.posts.map((x, i) => (
          <article key={i}>
            <strong>{profile.name}</strong>
            <p>{x.text}</p>
            {x.imageUrl && <img src={x.imageUrl} alt="" />}
            <small>{x.meta}</small>
          </article>
        ))}
      </div>
    ) : tab === "Connections" ? (
      <div className="ht-list">
        {["Thomas Berger · Mutual connection", "Helen Price · Investment Director", "Sofia Marin · Strategy", "Luca Romano · M&A"].map((x) => (
          <div className="ht-row" key={x}><span className="ht-person-mark">{x[0]}</span><strong>{x}</strong></div>
        ))}
      </div>
    ) : (
      <div className="ht-company">
        <div className="ht-company-logo">N</div>
        <h3>{profile?.company}</h3>
        <p>Independent strategy and technology investment firm focused on European growth companies.</p>
        <small>London · 51–200 employees · 18,420 followers</small>
        {profile?.companyUrl && <code className="ht-company-url">{profile.companyUrl}</code>}
      </div>
    );

  return (
    <section className={`holiday-tablet ht-stage-${stage}${certOpen ? " is-cert-open" : ""}`} style={style} aria-label="Elena's iPad">
      <div className="ht-status"><span>{config.time ?? "15:46"}</span><strong>{connected ? "Hotel_Guest" : "No Internet"}</strong><span>100%</span></div>

      {stage === "lost" && (
        <div className="ht-system-card">
          <span className="ht-spinner" />
          <h2>Internet connection unstable</h2>
          <p>The page stopped responding.</p>
          <div className="ht-offline">Connection lost</div>
          <button className="ht-primary cf-pulse-target ht-action-target" onClick={() => setStage("wifi")}>Open Wi-Fi settings<em className="ht-click-chip">Click here</em></button>
        </div>
      )}

      {(stage === "wifi" || stage === "connecting") && (
        <div className="ht-settings">
          <header><span>Settings</span><h2>Wi-Fi</h2></header>
          <div className="ht-wifi-toggle"><span>Wi-Fi</span><span className="ht-toggle on" /></div>
          <small>NETWORKS</small>
          {networks.map((n) => (
            <button
              key={n.id}
              className={`${!n.locked && n.id === "guest" ? "cf-pulse-target" : ""} ${n.id === "rogue" ? "ht-net-rogue" : ""}`}
              onClick={() => join(n.id, n.locked)}
              disabled={stage === "connecting"}
            >
              <span className="ht-net-name">{n.name}{!n.locked && n.id === "guest" && <em className="cf-click-here">Click here</em>}</span>
              <span className="ht-net-meta"><WifiIcon signal={n.signal} />{n.locked ? <LockIcon /> : <b>Open</b>}</span>
            </button>
          ))}
          {stage === "connecting" && <div className="ht-connecting"><span className="ht-spinner" /> Joining Hotel_Guest…</div>}
          {rogueNote && <div className="ht-rogue-toast">{rogueNote}</div>}
        </div>
      )}

      {stage === "wifi_risk" && (
        <div className="ht-wifi-risk">
          <header><small>PUBLIC WI-FI</small><h2>Which network is the hotel's?</h2></header>
          <p className="ht-risk-lead">Elena sees two open networks with almost the same name. Only one belongs to Ocean Resort. The other is a rogue access point run by someone nearby.</p>
          <div className="ht-ap-pair">
            <div className="ht-ap hotel">
              <WifiIcon signal={4} />
              <b>Hotel_Guest</b>
              <small>Ocean Resort</small>
              <span>Legitimate access point</span>
            </div>
            <div className="ht-ap-vs">?</div>
            <div className="ht-ap rogue">
              <WifiIcon signal={5} />
              <b>Hotel_Guest-Free</b>
              <small>Unknown operator</small>
              <span>Rogue access point</span>
            </div>
          </div>
          <p>A rogue access point copies a familiar name so people join the attacker’s network by mistake. The iPad cannot tell them apart.</p>
          <button className="ht-primary cf-pulse-target ht-action-target" onClick={() => setStage("wifi_ssl")}>How would you notice?<em className="ht-click-chip">Click here</em></button>
        </div>
      )}

      {stage === "wifi_ssl" && (
        <div className="ht-ssl-lab">
          <div className={`ht-chrome ${sslError ? "is-bad" : ""}`}>
            <div className="ht-chrome-tabs">
              <span className="ht-chrome-tab on">Gmail</span>
              <i />
            </div>
            <div className="ht-omnibox">
              <span className="ht-omnibox-nav" aria-hidden>← → ↻</span>
                <button
                  type="button"
                  className={`ht-ssl-lock${sslError ? " bad" : ""}`}
                  onClick={() => {
                    if (!sslError) return;
                    setLockOpen((open) => !open);
                    setCertOpen(false);
                  }}
                aria-label={sslError ? "Site information, connection is not secure" : "Secure connection"}
              >
                <SslLock bad={sslError} />
              </button>
              <button
                type="button"
                className="ht-omnibox-url"
                onClick={() => {
                  if (!sslError) return;
                  setLockOpen(true);
                  setCertOpen(false);
                }}
              >
                {sslError ? <s>https://</s> : "https://"}mail.google.com
              </button>
              {sslError && (
                <button
                  type="button"
                  className="ht-not-secure"
                  onClick={() => {
                    setLockOpen(true);
                    setCertOpen(false);
                  }}
                >
                  Not secure
                </button>
              )}
            </div>
            {lockOpen && !certOpen && (
              <div className="ht-site-info" role="dialog" aria-label="Site information">
                <small>Connection is not secure</small>
                <p>Attackers might be trying to steal your information from mail.google.com.</p>
                <button
                  type="button"
                  className="ht-cert-open cf-pulse-target"
                  onClick={() => {
                    setCertOpen(true);
                    setSawCert(true);
                  }}
                >
                  Certificate is not valid
                  <em className="cf-click-here">Click here</em>
                </button>
              </div>
            )}
            {certOpen && (
              <div className="ht-cert-sheet" role="dialog" aria-label="Certificate viewer">
                <header className="ht-cert-head">
                  <b>Certificate</b>
                  <button type="button" onClick={() => { setCertOpen(false); setLockOpen(false); }}>Close</button>
                </header>
                <div className="ht-cert-body">
                  <dl>
                    <div><dt>Issued to</dt><dd>{ROGUE_CERT.subject}</dd></div>
                    <div className="warn"><dt>Issued by</dt><dd>{ROGUE_CERT.issuer}<small>{ROGUE_CERT.org}</small><strong>{ROGUE_CERT.notGoogle}</strong></dd></div>
                    <div><dt>Validity</dt><dd>{ROGUE_CERT.valid}<small>Valid for 24 hours — typical of a captive-portal CA, not Google.</small></dd></div>
                    <div><dt>Serial number</dt><dd>{ROGUE_CERT.serial}</dd></div>
                    <div><dt>SHA-256 fingerprint</dt><dd><pre>{ROGUE_CERT.sha256}</pre></dd></div>
                  </dl>
                </div>
                <footer className="ht-cert-foot">
                  <button type="button" className="ht-primary cf-pulse-target" onClick={() => { setCertOpen(false); setLockOpen(false); }}>
                    I see — this is not Google<em className="ht-click-chip">Click here</em>
                  </button>
                </footer>
              </div>
            )}
            <div className="ht-chrome-page">
              {!sslError ? (
                <div className="ht-gmail-card">
                  <b>Sign in</b>
                  <span>to continue to Gmail</span>
                  <input type="text" readOnly autoComplete="off" value="elena.fischer@aureon.example" />
                  <button className="ht-primary cf-pulse-target ht-action-target" onClick={() => setSslError(true)}>
                    Next
                    <em className="ht-click-chip">Click here</em>
                  </button>
                </div>
              ) : (
                <div className="ht-chrome-interstitial">
                  <div className="ht-ssl-mark" aria-hidden>
                    <svg viewBox="0 0 24 24"><path d="M12 2 22 20H2L12 2z" fill="currentColor" /><rect x="11" y="9" width="2" height="6" fill="#fff" /><rect x="11" y="16.4" width="2" height="2" fill="#fff" /></svg>
                  </div>
                  <h3>Your connection is not private</h3>
                  <p>Attackers might be trying to steal your information from <b>mail.google.com</b> (for example, passwords, messages, or credit cards).</p>
                  <code>NET::ERR_CERT_AUTHORITY_INVALID</code>
                  <div className="ht-ssl-actions">
                    <button
                      type="button"
                      className="ht-ssl-safety"
                      onClick={() => setSafetyNote(true)}
                    >
                      Back to safety
                    </button>
                    <button
                      type="button"
                      className={`ht-ssl-advanced${advancedOpen ? " on" : ""}${sawCert && !advancedOpen ? " cf-pulse-target" : ""}`}
                      onClick={() => setAdvancedOpen((v) => !v)}
                    >
                      {advancedOpen ? "Hide advanced" : "Advanced"}
                      {sawCert && !advancedOpen && <em className="cf-click-here">Click here</em>}
                    </button>
                  </div>
                  {safetyNote && (
                    <p className="ht-ssl-safety-note">That is the correct choice on a real network. You can still inspect the lock and Advanced here, or continue.</p>
                  )}
                  <div className="ht-ssl-lab-note">
                    <b>Optional</b>
                    <p>Tap the red lock, Not secure, or Advanced to see how a browser exposes a fake certificate. You do not have to — continue when you are ready.</p>
                    <div className="ht-ssl-key-point">
                      <strong>The warning itself is protecting Elena.</strong>
                      <p>
                        The suspicious part is that a well-known site such as <b>mail.google.com</b> is suddenly
                        presenting a certificate warning. Google normally has a valid certificate. On an unfamiliar
                        network, this can mean that the network is intercepting the connection or redirecting Elena
                        somewhere else. She should stop rather than enter a password or bypass the warning.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="ht-primary cf-pulse-target ht-action-target"
                      onClick={() => setStage("connected")}
                    >
                      Continue<em className="ht-click-chip">Click here</em>
                    </button>
                  </div>
                  {advancedOpen && (
                    <div className="ht-ssl-advanced-body">
                      <p>This server could not prove that it is mail.google.com. Its security certificate is not trusted by this iPad. That often means a network in the middle — here, the rogue access point — is presenting its own certificate.</p>
                      <button
                        type="button"
                        className={`ht-ssl-proceed${sawCert ? " cf-pulse-target" : " dim"}`}
                        disabled={!sawCert}
                        onClick={() => sawCert && setStage("connected")}
                      >
                        {sawCert ? <>Proceed to mail.google.com (unsafe)<em className="cf-click-here">Click here</em></> : "Open the certificate in the address bar first"}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {stage === "connected" && (
        <div className="ht-system-card ht-success">
          <span className="ht-check">✓</span>
          <h2>Connected</h2>
          <p>Hotel_Guest</p>
          <button className="ht-primary ht-action-target" onClick={() => setStage("hunt")}>Return to iPad<em className="ht-click-chip">Click here</em></button>
        </div>
      )}

      {stage === "hunt" && (
        <HolidayRogueHunt
          onDone={() => onComplete("connected")}
          onStageChange={onStageChange}
          doneLabel={config.continueLabel}
        />
      )}

      {stage === "desktop" && (
        <div className="ht-home">
          <div className="ht-owner">{config.ownerName ?? "Elena's company iPad"}</div>
          <div className="ht-app-grid">
            {APPS.map((app) => (
              <button key={app} className={app === "Mail" ? "cf-pulse-target ht-action-target" : ""} onClick={() => app === "Mail" && setStage("inbox")}>
                <span className={`ht-app-icon ht-app-${app.toLowerCase()}`}>{app === "Mail" ? <MailIcon /> : app.slice(0, 2)}</span>
                <small>{app}</small>
                {app === "Mail" && <i>1</i>}
                {app === "Mail" && <em className="ht-click-chip">Click here</em>}
              </button>
            ))}
          </div>
        </div>
      )}

      {stage === "home" && (
        <div className="ht-home">
          <div className="ht-owner">{config.ownerName ?? "Elena's company iPad"}</div>
          <div className="ht-app-grid">
            {["Mail", "Browser", "Teams", "Files", "Calendar", "Game"].map((app) => (
              <button
                key={app}
                className={app === "Game" ? "cf-pulse-target ht-action-target" : ""}
                onClick={() => app === "Game" && onComplete("open_game")}
              >
                <span className={`ht-app-icon ${app === "Game" ? "ht-app-game" : `ht-app-${app.toLowerCase()}`}`}>
                  {app === "Game" ? "AI?" : app.slice(0, 2)}
                </span>
                <small>{app === "Game" ? "Real or not?" : app}</small>
                {app === "Game" && <em className="ht-click-chip">Click here</em>}
              </button>
            ))}
          </div>
        </div>
      )}

      {stage === "inbox" && (
        <div className="ht-mail">
          <aside>
            <h3>Inbox</h3>
            {[
              ["LinkedIn", "Daniel Kovacs wants to connect with you", "Now"],
              ["Ocean Resort", "Your spa booking is confirmed", "14:12"],
              ["Aureon HR", "Half-year town hall recording", "Fri"],
              ["Thomas Berger", "Enjoy the break — you earned it", "Thu"],
            ].map((m, i) => (
              <button key={m[1]} className={i === 0 ? "unread cf-pulse-target ht-action-target" : ""} onClick={() => i === 0 && setStage("mail")}>
                <strong>{m[0]}</strong><span>{m[1]}</span><small>{m[2]}</small>
                {i === 0 && <em className="ht-click-chip">Click here</em>}
              </button>
            ))}
          </aside>
          <main>
            <button className="ht-mail-preview cf-pulse-target" onClick={() => setStage("mail")}>
              <header><span className="ht-li-mark">in</span><div><strong>LinkedIn</strong><small>notifications@linkedin.com</small></div><time>Now</time></header>
              <h2>Daniel Kovacs wants to connect with you</h2>
              <p>Hey Elena, we both know Thomas Berger. I saw your presentation at CyberSec Europe last year…</p>
              <span className="ht-preview-fade">Open the message to read more</span>
            </button>
          </main>
        </div>
      )}

      {stage === "mail" && (
        <div className="ht-mail-open">
          <header><button onClick={() => setStage("inbox")}>Inbox</button><span>LinkedIn</span></header>
          <div className="ht-li-mark">in</div>
          <h2>Daniel Kovacs wants to connect with you on LinkedIn</h2>
          <p>Hey Elena, we both know Thomas Berger. I saw your presentation at CyberSec Europe last year. Really interesting perspective on finance automation.</p>
          <button className="ht-primary cf-pulse-target ht-action-target" onClick={() => onComplete("view_profile")}>View profile <em className="ht-click-chip">Click here</em></button>
        </div>
      )}

      {stage === "profile" && profile && (
        <div className="ht-linkedin">
          <header><span className="ht-li-mark">in</span><div className="ht-search">Search</div><span>Home · My Network · Messages</span></header>
          <div className="ht-profile-hero">
            <div className="ht-cover" />
            {profile.avatarUrl ? <img src={profile.avatarUrl} alt={profile.name} /> : <div className="ht-profile-avatar">DK</div>}
            <div className="ht-profile-copy">
              <h2>{profile.name}</h2><p>{profile.title}</p><small>{profile.location} · {profile.connections} connections</small>
              <strong>{profile.mutual}</strong>
            </div>
            <div className="ht-connect-actions">
              {seenTabs.size >= TABS.length && (
                <button type="button" onClick={() => { setIgnored(true); setStage("ignored_note"); }}>
                  Ignore
                </button>
              )}
              <button
                className="ht-primary"
                disabled={seenTabs.size < TABS.length}
                onClick={() => seenTabs.size >= TABS.length && setStage("messages")}
              >
                {seenTabs.size >= TABS.length ? "Accept connection" : `Open every tab first (${seenTabs.size}/${TABS.length})`}
              </button>
            </div>
          </div>
          <nav>
            {TABS.map((x) => {
              const current = tab === x;
              const seen = seenTabs.has(x);
              return (
                <button
                  key={x}
                  type="button"
                  className={`${current ? "on" : ""} ${seen ? "seen" : "unseen cf-pulse-target"}`}
                  onClick={() => {
                    setTab(x);
                    setSeenTabs((old) => new Set(old).add(x));
                  }}
                >
                  <span>{x}</span>
                  {!seen && <em className="ht-tab-todo">to open</em>}
                  {seen && !current && <em className="ht-tab-done" aria-hidden>✓</em>}
                </button>
              );
            })}
          </nav>
          <div className="ht-profile-content">{profileBody}</div>
        </div>
      )}

      {stage === "ignored_note" && profile && (
        <div className="ht-li-payoff">
          <small>GOOD CALL</small>
          <h2>Elena doesn't know Daniel, so she leaves the request.</h2>
          <p>A complete profile and a mutual connection are not proof of who someone is. Ignoring a stranger costs nothing; a real contact can reach Elena another way.</p>
          <p>Here is what would have happened if she had accepted.</p>
          <button className="ht-primary cf-pulse-target ht-action-target" onClick={() => setStage("messages")}>
            See what would have happened
          </button>
        </div>
      )}

      {stage === "messages" && profile && (
        <div className="ht-linkedin ht-linkedin-messages">
          <header><span className="ht-li-mark">in</span><div className="ht-search">Search</div><button className="ht-message-control cf-pulse-target ht-action-target" onClick={() => setStage("conversation")}><span>▱</span> Messages <i>1</i><em className="ht-click-chip">Click here</em></button></header>
          <div className="ht-connected-card">
            <span className="ht-check">✓</span>
            <div><h2>{ignored ? "If Elena had accepted" : "Connection accepted"}</h2><p>Daniel can now message Elena directly.</p></div>
          </div>
        </div>
      )}

      {stage === "conversation" && profile && (
        <div className="ht-li-conversation">
          <header><button onClick={() => setStage("messages")}>← Messages</button><strong>Daniel Kovacs</strong><span>● Online</span></header>
          <main>
            <div className="ht-li-contact">{profile.avatarUrl && <img src={profile.avatarUrl} alt="" />}<div><b>{profile.name}</b><small>{profile.title}</small></div></div>
            <div className="ht-li-bubble"><p>{profile.message}</p><small>Now</small></div>
          </main>
          <footer><span>Write a message…</span><button className="ht-primary cf-pulse-target ht-action-target" onClick={() => setStage("payoff")}>What does this message mean?<em className="ht-click-chip">Click here</em></button></footer>
        </div>
      )}

      {stage === "payoff" && profile && (
        <div className="ht-li-payoff">
          <small>{ignored ? "IF ELENA HAD ACCEPTED" : "AFTER THE ACCEPT"}</small>
          <h2>That hello is the payload.</h2>
          <p>Daniel now has a private thread with Elena. He did not send a file or a link. He used two facts anyone can scrape from LinkedIn: a mutual connection (Thomas Berger) and a conference Elena actually attended.</p>
          <ul>
            <li><b>Do not accept a connection merely because the profile looks complete.</b> If you do not genuinely know the person, they may be a scammer building access and trust.</li>
            <li>A long profile, articles and 500+ connections can be grown for months before an attack.</li>
            <li>A real mutual does not prove the sender is who they claim to be.</li>
            <li>A company page with thousands of followers can still be a shell firm.</li>
          </ul>
          <button className="ht-primary cf-pulse-target ht-action-target" onClick={() => onComplete(ignored ? "ignored" : "accepted")}>
            Start the fake-profile game<em className="ht-click-chip">Click here</em>
          </button>
        </div>
      )}

      {stage === "reveal" && profile && (
        <div className="ht-reveal">
          <div className="ht-reveal-profile">
            {profile.avatarUrl ? <img src={profile.avatarUrl} alt="" /> : <div className="ht-profile-avatar">DK</div>}
            <div><small>ATTACKER ANNOTATION</small><h2>{profile.name}</h2><strong>FAKE IDENTITY</strong></div>
          </div>
          <div className="ht-reveal-grid">
            {(config.revealNotes ?? []).map((n) => (
              <div key={n.label} className={n.tone === "risk" ? "risk" : ""}><span>{n.label}</span><strong>{n.value}</strong></div>
            ))}
          </div>
          <div className="ht-network-strip">
            <span>Elena</span><i>→</i><span>Marc Weber</span><i>·</i><span>Sarah Lindner</span><i>·</i><span>Project Horizon</span>
          </div>
          <blockquote>The request never contained malware. It did not need to. Its value was trust, access and information.</blockquote>
          <button className="ht-primary cf-pulse-target" onClick={() => onComplete("continue")}>{config.continueLabel ?? "Continue"}</button>
        </div>
      )}
    </section>
  );
}
